<script setup lang="ts">
import { useToast } from '@solfacil/girassol';
import type { Option } from '@solfacil/girassol/dist/types/components/forms/select/types';

import { useCancelOrderMutation } from '~/types/ecommerce/generated.codegen';

const { createErrorToast, createSuccessToast } = useToast();

const props = defineProps<{
  open: boolean;
  orderId: string;
  orderNumber: string;
  clientName?: string;
}>();

const emit = defineEmits<{
  (event: 'update'): void;
  (event: 'close'): void;
}>();

const cancelationReason = ref<Option>({
  name: '',
  value: ''
});

watch(
  () => props.open,
  (open) => {
    if (!open) {
      cancelationReason.value = {
        name: '',
        value: ''
      };
    }
  }
);

const CANCELATION_REASONS: Option[] = [
  { name: 'Alteração de equipamento', value: 'Alteração de equipamento' },
  {
    name: 'Alteração de meio de pagamento',
    value: 'Alteração de meio de pagamento'
  },
  { name: 'Alteração de endereço', value: 'Alteração de endereço' },
  {
    name: 'Dados divergentes (email, nome, documento, etc)',
    value: 'Dados divergentes (email, nome, documento, etc)'
  },
  { name: 'Duplicidade', value: 'Duplicidade' },
  { name: 'Desistência', value: 'Desistência' },
  {
    name: 'Erro humano (erros de digitação por exemplo)',
    value: 'Erro humano (erros de digitação por exemplo)'
  },
  { name: 'Ruptura de estoque', value: 'Ruptura de estoque' },
  { name: 'Erro sistêmico', value: 'Erro sistêmico' },
  { name: 'Outros', value: 'Outros' }
];

const {
  mutate: cancelOrderMutate,
  onDone: cancelOrderResult,
  onError: cancelOrderError,
  loading: cancelOrderLoading
} = useCancelOrderMutation();
cancelOrderResult(() => {
  createSuccessToast({
    title: 'Pedido de cancelamento recebido',
    description: `Em breve o pedido do cliente ${props.clientName} será cancelado`
  });
  emit('close');
  emit('update');
});
cancelOrderError(() => {
  createErrorToast('Não foi possível cancelar o pedido');
});

function cancelOrder () {
  createSuccessToast({
    title: `Cancelando pedido: ${props.orderId}`,
    description: `Motivo do cancelamento: ${String(cancelationReason.value?.name)}`
  });
  cancelOrderMutate({
    order_id: props.orderId,
    cancelation_reason: String(cancelationReason.value?.name)
  });
}
</script>

<template>
  <SolModal
    id="cancel-modal"
    :is-open="open"
    title="Cancelamento de pedido"
    action-secondary-text="Fechar"
    class="cancel-modal"
    :size="{
      desktop: 'medium',
      mobile: 'bottom-sheet',
    }"
    @action:secondary="$emit('close')"
    @close="$emit('close')"
  >
    <div class="cancel-modal">
      <SharedImgLazy
        src="/images/info.svg"
        alt="info-image"
        class="info-image"
      />
      <span class="info-text">
        Tem certeza que deseja cancelar o pedido número
        <b>{{ orderNumber }}</b>, do(a) cliente <b>{{ clientName }}</b>?
      </span>
      <span class="info-text">
        Para continuar, nos diga o motivo do cancelamento do seu pedido.
      </span>

      <SolSelect
        id="cancelation-reason"
        v-model:selected="cancelationReason"
        dropdown-position="top"
        name="cancelation-reason"
        label="Motivo do cancelamento*"
        placeholder="Selecione um motivo"
        class="w-full"
        :options="CANCELATION_REASONS"
      />
    </div>
    <template #action-primary>
      <SolButton
        id="cancel-order"
        :disabled="!cancelationReason.value"
        :loading="cancelOrderLoading"
        variant="primary"
        size="medium"
        @click="() => cancelOrder()"
      >
        Enviar pedido de cancelamento
      </SolButton>
    </template>
  </SolModal>
</template>

<style lang="scss">
.cancel-modal {
  @apply flex flex-col gap-4xs;

  .info-image {
    @apply w-16 h-20 mx-auto;
  }

  .info-text {
    @apply fonts-body-large-regular text-neutral-low-dark;
  }

  .select-options-container {
    max-height: 200px;
    .select-list-core {
      max-height: 200px;
    }
  }
}
</style>
